// Facility billing type → claim form. Mirrors the backend's authoritative mapping in
// src/utils/resolveFormType.js — keep the two in step.
//
// 'Professional' and 'Technical' BOTH produce a CMS-1500: billing only the technical
// component of a diagnostic test (modifier TC) is a pricing concern on the professional
// claim, not a different form. 'Global' means "institutional claim on the UB-04" in this
// system, which is why it — and only it — maps to CMS-1450.
//
// This mapping was previously inlined as `=== 'Professional' ? 'CMS-1500' : 'CMS-1450'`
// in four places, all of which would have silently filed technical claims as UB-04s.

export const BILLING_TYPES = ['Professional', 'Technical', 'Global'];

export const BILLING_TYPE_LABELS = {
  Professional: 'Professional (CMS-1500)',
  Technical:    'Technical component (CMS-1500)',
  Global:       'Global / Institutional (CMS-1450)',
};

const BILLING_TYPE_FORMS = {
  Professional: 'CMS-1500',
  Technical:    'CMS-1500',
  Global:       'CMS-1450',
};

// The form a facility defaults its claims to. A payer's accepted_form_types rule can still
// override this downstream — that resolution lives on the server.
export function facilityDefaultForm(billingType) {
  return BILLING_TYPE_FORMS[billingType] ?? 'CMS-1450';
}

// The billing component a facility defaults its claims to (claims.billing_component).
// Values match billing_type apart from case.
export function facilityDefaultComponent(billingType) {
  const key = String(billingType || '').toLowerCase();
  return ['professional', 'technical', 'global'].includes(key) ? key : 'professional';
}

// The component a facility actually bills, given the facility OBJECT: its configured
// defaultBillingComponent if it has one, otherwise the billingType fallback above.
//
// The twin of resolveFacilityComponent in the server's utils/resolveFormType.js, and kept a
// separate function there for the same reason it is one here: the fallback mapping and the
// configured override are different questions, and the name should say which is being asked.
//
// This mirror is load-bearing, not decoration. The override exists so a technical facility
// can be switched to global billing by configuration — and a switch that reached only the
// server would leave the biller looking at the OLD component's required fields (the
// create-encounter form would keep demanding a referring provider instead of a rendering
// one). The server still re-applies every rule on save; this is what makes the form agree.
export function resolveFacilityComponent(facility) {
  const configured = String(facility?.defaultBillingComponent || '').toLowerCase();
  if (['professional', 'technical', 'global'].includes(configured)) return configured;
  return facilityDefaultComponent(facility?.billingType);
}

// ---------------------------------------------------------------------------
// Billing component → pricing modifier and fee-schedule column
//
// Mirrors src/utils/billingComponent.js on the server, which is authoritative: it re-applies
// these rules on save, so a stale client can't file a technical claim without its TC. The
// client copy exists so the biller SEES the modifier and the right charge before submitting.
// ---------------------------------------------------------------------------

export const BILLING_COMPONENTS = ['professional', 'technical', 'global'];

export const COMPONENT_LABELS = {
  professional: 'Professional only (26)',
  technical:    'Technical only (TC)',
  global:       'Global (26 + TC)',
};

export const COMPONENT_MODIFIERS = {
  professional: '26',
  technical:    'TC',
  global:       null,
};

// How a claim's form + component read together, for anywhere a claim is described rather
// than edited (claim detail, review modal). The form name alone is ambiguous once technical
// billing exists: a TC-only claim and a full professional claim are both "CMS-1500", and they
// are paid very differently.
//
// Only a CMS-1500 gets a component suffix. On a CMS-1450 the component is always `global`,
// which in this system is just another word for institutional — repeating it says nothing.
export function claimFormLabel(formType, billingComponent) {
  if (formType === 'CMS-1450') return 'Institutional (CMS-1450)';
  if (formType !== 'CMS-1500') return '—';

  const suffix = {
    technical: 'Technical component only (TC)',
    global:    'Global — both components',
  }[billingComponent];

  return suffix ? `Professional (CMS-1500) — ${suffix}` : 'Professional (CMS-1500)';
}

const PRICING_MODIFIERS = ['26', 'TC'];
export const MAX_MODIFIERS = 4;

export function parseModifiers(value) {
  const raw = Array.isArray(value) ? value : String(value ?? '').split(/[,\s]+/);
  return raw
    .map((m) => String(m ?? '').trim().toUpperCase())
    .filter((m) => m && m !== '-');
}

// The component owns the pricing slot: any 26/TC already present is replaced, never stacked,
// and the result leads with the component's modifier. Pricing modifiers must come first —
// carriers read the first position to price the line. When the code's PC/TC indicator says it
// cannot carry the modifier, none is applied (an indicator-3 code already IS the technical
// component); an unknown indicator is not treated as ineligible.
export function resolveLineModifiers(existing, component, pcTcIndicator) {
  const pricing = permitsPricingModifier(component, pcTcIndicator)
    ? COMPONENT_MODIFIERS[component] ?? null
    : null;
  const others  = parseModifiers(existing).filter((m) => !PRICING_MODIFIERS.includes(m));

  const seen = new Set();
  const kept = others.filter((m) => (seen.has(m) ? false : seen.add(m)));

  const all = (pricing ? [pricing, ...kept] : kept).slice(0, MAX_MODIFIERS);
  return all.length ? all.join(',') : '';
}

// Charge for a fee-schedule row under a billing component. Facility override wins over the
// reference price; falls back to the global charge when the component has no price on file.
export function resolveLineCharge(row, component) {
  if (!row) return null;

  const pick = (...vals) => {
    for (const v of vals) {
      if (v == null || v === '') continue;
      const n = parseFloat(v);
      if (Number.isFinite(n)) return n;
    }
    return null;
  };

  const global = pick(row.custom_charge, row.default_charge);

  if (component === 'technical') {
    return pick(row.custom_technical_charge, row.technical_charge) ?? global;
  }
  if (component === 'professional') {
    return pick(row.custom_professional_charge, row.professional_charge) ?? global;
  }
  return global;
}

// Whether a code may carry 26/TC per its CMS PC/TC indicator. null = no reference data for
// the code (billers may key codes that aren't on the fee schedule), which is not "ineligible".
export function isSplitBillable(pcTcIndicator) {
  if (pcTcIndicator == null || pcTcIndicator === '') return null;
  return String(pcTcIndicator).trim() === '1';
}

// ---------------------------------------------------------------------------
// Code eligibility for a billing component (CMS PC/TC indicator)
//
// Mirrors codeComponentIssue in src/utils/billingComponent.js. The server blocks these at
// transmission; showing them on the form is what keeps a biller from finishing a claim that
// can't be sent.
// ---------------------------------------------------------------------------

const PC_TC_MEANING = {
  '0': 'the PC/TC concept does not apply to it (it is a physician service, not a test)',
  '1': 'both components are separately billable',
  '2': 'it is a professional-component-only code',
  '3': 'it is a technical-component-only code — the code already IS the technical component',
  '4': 'it is a global-test-only code and is never split',
  '5': 'it is an incident-to code',
  '6': 'it is a laboratory physician interpretation code',
  '7': 'it is a physical therapy service',
  '8': 'it is a physician interpretation code for a clinical lab test',
  '9': 'the PC/TC concept does not apply to it',
};

const ALLOWS_MODIFIER = { '26': ['1', '6'], TC: ['1'] };
const REDUNDANT_FOR   = { '26': ['2', '8'], TC: ['3'] };

const normalizeIndicator = (v) => (v == null || v === '' ? null : String(v).trim());

export function permitsPricingModifier(component, pcTcIndicator) {
  const modifier = COMPONENT_MODIFIERS[component] ?? null;
  if (!modifier) return false;
  const ind = normalizeIndicator(pcTcIndicator);
  if (ind === null) return true;
  return ALLOWS_MODIFIER[modifier].includes(ind);
}

// { level: 'error' | 'warning', message } or null when the code is fine as-is.
export function codeComponentIssue(component, pcTcIndicator) {
  const modifier = COMPONENT_MODIFIERS[component] ?? null;
  if (!modifier) return null;
  const ind = normalizeIndicator(pcTcIndicator);
  if (ind === null) return null;
  if (ALLOWS_MODIFIER[modifier].includes(ind)) return null;

  if (REDUNDANT_FOR[modifier].includes(ind)) {
    return {
      level: 'warning',
      message: `${PC_TC_MEANING[ind]}, so no ${modifier} modifier is applied — bill it unmodified`,
    };
  }
  return {
    level: 'error',
    message: `cannot be billed as the ${component} component: ${PC_TC_MEANING[ind] || `its PC/TC indicator is ${ind}`}`,
  };
}

// Which provider an encounter must name, given the component it will be billed as.
// Mirrors missingEncounterProvider() in the backend's utils/billingComponent.js: a technical
// claim names the ORDERING physician (837P loop 2310A / box 17), not a rendering one — nobody
// "renders" the technical half of a study. Returns 'referring' or 'rendering'.
export function requiredEncounterProvider(component) {
  return component === 'technical' ? 'referring' : 'rendering';
}
